import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import DashLayout from "@/components/DashLayout";
import api from "@/lib/api";
import { toast } from "sonner";
import { Video, Copy, Plus, ExternalLink, Pencil, Trash2, X, Save, CalendarClock } from "lucide-react";

const PROVIDER_LABEL = { jitsi: "Jitsi", teams: "Teams", zoom: "Zoom", meet: "Google Meet" };

export default function ClinicianMeetings() {
  const { t } = useTranslation();
  const [meetings, setMeetings] = useState([]);
  const [patients, setPatients] = useState([]);
  const [show, setShow] = useState(false);
  const [f, setF] = useState({ provider: "jitsi", title: "Consultation", patient_id: "", external_url: "" });
  const [editing, setEditing] = useState(null); // meeting object being edited
  const [rescheduling, setRescheduling] = useState(null); // meeting being rescheduled
  const [newSchedule, setNewSchedule] = useState("");

  const refresh = async () => {
    const [m, p] = await Promise.all([api.get("/meetings/mine"), api.get("/patients")]);
    setMeetings(m.data); setPatients(p.data);
  };
  useEffect(() => { refresh(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      const payload = { provider: f.provider, title: f.title, patient_id: f.patient_id || null };
      if (f.provider !== "jitsi") payload.external_url = f.external_url;
      const { data } = await api.post("/meetings", payload);
      toast.success("Meeting created");
      navigator.clipboard?.writeText(data.url).catch(() => {});
      setF({ provider: "jitsi", title: "Consultation", patient_id: "", external_url: "" });
      setShow(false); refresh();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const copy = (url) => { navigator.clipboard?.writeText(url); toast.success("Link copied"); };

  const saveEdit = async () => {
    if (!editing?.title?.trim()) return toast.error("Title is required");
    if (!editing?.url?.trim()) return toast.error("Meeting link is required");
    try {
      const payload = {
        title: editing.title.trim(),
        provider: editing.provider,
        url: editing.url.trim(),
        patient_id: editing.patient_id || null,
        scheduled_at: editing.scheduled_at || null,
      };
      await api.patch(`/meetings/${editing.id}`, payload);
      toast.success("Meeting updated — participants notified");
      setEditing(null);
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update"); }
  };

  const removeMeeting = async (m) => {
    if (!window.confirm(`Cancel the meeting "${m.title}"?\nThe patient (if linked) will be notified that it was cancelled.`)) return;
    try {
      await api.delete(`/meetings/${m.id}`);
      setMeetings((prev) => prev.filter((x) => x.id !== m.id));
      toast.success("Meeting cancelled");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not cancel"); }
  };

  const openReschedule = (m) => {
    setRescheduling(m);
    setNewSchedule(m.scheduled_at ? m.scheduled_at.slice(0, 16) : "");
  };

  const saveReschedule = async () => {
    if (!newSchedule) return toast.error("Pick a new date and time");
    try {
      await api.patch(`/meetings/${rescheduling.id}`, {
        scheduled_at: new Date(newSchedule).toISOString(),
      });
      toast.success("Meeting rescheduled — participants notified");
      setRescheduling(null);
      setNewSchedule("");
      refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not reschedule"); }
  };

  return (
    <DashLayout>
      <div className="flex justify-between items-end mb-8">
        <div>
          <div className="fl-label text-[#f47a20]">{t("pages.cMeetings.eyebrow")}</div>
          <h1 className="fl-heading text-4xl mt-2">{meetings.length} sessions</h1>
        </div>
        <button className="fl-btn" onClick={() => setShow(!show)} data-testid="meet-new"><Plus size={14} /> New meeting</button>
      </div>

      {show && (
        <form onSubmit={create} className="fl-card p-6 mb-6 grid md:grid-cols-2 gap-3" data-testid="meet-form">
          <div>
            <div className="fl-label mb-1">Provider</div>
            <select className="fl-input" value={f.provider} onChange={(e) => setF({ ...f, provider: e.target.value })} data-testid="meet-provider">
              <option value="jitsi">Jitsi (instant, free)</option>
              <option value="teams">Microsoft Teams</option>
              <option value="zoom">Zoom</option>
              <option value="meet">Google Meet</option>
            </select>
          </div>
          <div>
            <div className="fl-label mb-1">Title</div>
            <input className="fl-input" required value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="meet-title" />
          </div>
          <div>
            <div className="fl-label mb-1">Patient (optional)</div>
            <select className="fl-input" value={f.patient_id} onChange={(e) => setF({ ...f, patient_id: e.target.value })} data-testid="meet-patient">
              <option value="">— none —</option>
              {patients.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          {f.provider !== "jitsi" && (
            <div>
              <div className="fl-label mb-1">Paste meeting URL</div>
              <input className="fl-input" required placeholder={`https://${f.provider === "teams" ? "teams.microsoft.com" : f.provider === "zoom" ? "us02web.zoom.us" : "meet.google.com"}/…`}
                value={f.external_url} onChange={(e) => setF({ ...f, external_url: e.target.value })} data-testid="meet-url" />
            </div>
          )}
          <button className="fl-btn md:col-span-2 justify-center" data-testid="meet-create">Create meeting</button>
        </form>
      )}

      <div className="grid md:grid-cols-2 gap-4">
        {meetings.map((m) => {
          const providerLabel = PROVIDER_LABEL[m.provider] || m.provider;
          const patient = patients.find((p) => p.id === m.patient_id);
          return (
            <div key={m.id} className="fl-card p-5 relative" data-testid={`meet-${m.id}`}>
              {/* Cancel = bin icon in the top-right corner (destructive intent) */}
              <button
                onClick={() => removeMeeting(m)}
                className="absolute top-3 right-3 p-1.5 rounded-md text-zinc-500 hover:text-red-400 hover:bg-red-500/10 transition-colors"
                title="Cancel this meeting"
                data-testid={`meet-remove-${m.id}`}
              >
                <Trash2 size={15} />
              </button>
              <div className="flex items-center gap-2 pr-8">
                <Video className="text-[#f47a20]" size={16} />
                <div className="fl-label">{providerLabel}</div>
              </div>
              <div className="fl-heading text-lg mt-2 pr-8">{m.title}</div>
              {patient && <div className="text-xs text-zinc-500 mt-1">with {patient.name}</div>}
              <div className="text-xs text-zinc-500 mt-1">Created {new Date(m.created_at).toLocaleString("en-GB")}</div>
              {m.scheduled_at && (
                <div className="text-xs text-[color:var(--fl-orange)] mt-1">
                  Scheduled: {new Date(m.scheduled_at).toLocaleString("en-GB")}
                </div>
              )}
              <div className="mt-4 flex flex-wrap gap-2">
                <a href={m.url} target="_blank" rel="noreferrer" className="fl-btn text-xs" data-testid={`meet-join-${m.id}`}><ExternalLink size={12} /> Join</a>
                <button onClick={() => copy(m.url)} className="fl-btn-ghost text-xs" data-testid={`meet-copy-${m.id}`}><Copy size={12} /> Copy link</button>
                <button
                  onClick={() => setEditing({ ...m })}
                  className="fl-btn-ghost text-xs"
                  title="Edit title, link, provider or patient"
                  data-testid={`meet-edit-${m.id}`}
                >
                  <Pencil size={12} /> Edit
                </button>
                <button
                  onClick={() => openReschedule(m)}
                  className="fl-btn-ghost text-xs"
                  title="Change the date and time of this meeting"
                  data-testid={`meet-reschedule-${m.id}`}
                >
                  <CalendarClock size={12} /> Reschedule
                </button>
              </div>
              <div className="mt-3 text-[11px] text-zinc-500 break-all font-mono">{m.url}</div>
            </div>
          );
        })}
        {meetings.length === 0 && <div className="fl-card p-6 text-sm text-zinc-500 md:col-span-2">No meetings yet. Start a Jitsi room in one click — no accounts needed.</div>}
      </div>

      {/* -------- Reschedule modal (quick date/time change) -------- */}
      {rescheduling && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setRescheduling(null)}>
          <div className="fl-card p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="meet-reschedule-modal">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="fl-label text-[#f47a20] flex items-center gap-1"><CalendarClock size={12} /> Reschedule</div>
                <div className="fl-heading text-lg mt-1">{rescheduling.title || "Meeting"}</div>
              </div>
              <button onClick={() => setRescheduling(null)}><X size={16} /></button>
            </div>
            {rescheduling.scheduled_at && (
              <div className="text-xs mb-3" style={{ color: "var(--fl-muted)" }}>
                Current time: <b>{new Date(rescheduling.scheduled_at).toLocaleString("en-GB")}</b>
              </div>
            )}
            <label className="flex flex-col gap-1">
              <span className="fl-label">New date & time</span>
              <input
                type="datetime-local"
                className="fl-input"
                value={newSchedule}
                onChange={(e) => setNewSchedule(e.target.value)}
                autoFocus
                data-testid="meet-reschedule-input"
              />
            </label>
            <div className="text-[11px] mt-2" style={{ color: "var(--fl-muted-2)" }}>
              The linked patient (if any) will be notified of the new time.
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setRescheduling(null)} className="fl-btn-ghost text-sm">Cancel</button>
              <button onClick={saveReschedule} className="fl-btn text-sm" data-testid="meet-reschedule-save">
                <Save size={12} /> Save new time
              </button>
            </div>
          </div>
        </div>
      )}

      {/* -------- Edit modal -------- */}
      {editing && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div className="fl-card p-6 w-full max-w-lg" onClick={(e) => e.stopPropagation()} data-testid="meet-edit-modal">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="fl-label text-[#f47a20]">Edit meeting</div>
                <div className="fl-heading text-lg mt-1">{editing.title || "Meeting"}</div>
              </div>
              <button onClick={() => setEditing(null)} data-testid="meet-edit-close"><X size={16} /></button>
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              <label className="flex flex-col gap-1 md:col-span-2">
                <span className="fl-label">Title</span>
                <input className="fl-input" value={editing.title || ""} onChange={(e) => setEditing({ ...editing, title: e.target.value })} data-testid="meet-edit-title" />
              </label>
              <label className="flex flex-col gap-1">
                <span className="fl-label">Provider</span>
                <select className="fl-input" value={editing.provider || "jitsi"} onChange={(e) => setEditing({ ...editing, provider: e.target.value })} data-testid="meet-edit-provider">
                  {Object.entries(PROVIDER_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                </select>
              </label>
              <label className="flex flex-col gap-1">
                <span className="fl-label">Patient (optional)</span>
                <select className="fl-input" value={editing.patient_id || ""} onChange={(e) => setEditing({ ...editing, patient_id: e.target.value })} data-testid="meet-edit-patient">
                  <option value="">— none —</option>
                  {patients.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </label>
              <label className="flex flex-col gap-1 md:col-span-2">
                <span className="fl-label">Meeting URL / link</span>
                <input className="fl-input font-mono text-sm" value={editing.url || ""} onChange={(e) => setEditing({ ...editing, url: e.target.value })} data-testid="meet-edit-url" />
              </label>
              <label className="flex flex-col gap-1 md:col-span-2">
                <span className="fl-label">Scheduled for (optional)</span>
                <input
                  type="datetime-local"
                  className="fl-input"
                  value={editing.scheduled_at ? editing.scheduled_at.slice(0, 16) : ""}
                  onChange={(e) => setEditing({ ...editing, scheduled_at: e.target.value ? new Date(e.target.value).toISOString() : null })}
                  data-testid="meet-edit-schedule"
                />
              </label>
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setEditing(null)} className="fl-btn-ghost text-sm">Cancel</button>
              <button onClick={saveEdit} className="fl-btn text-sm" data-testid="meet-edit-save">
                <Save size={12} /> Save changes
              </button>
            </div>
          </div>
        </div>
      )}
    </DashLayout>
  );
}
